import { useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { updateProfile, getProfile } from "@/api/profile";
import { NOTIFICATION_PREFERENCES_KEY } from "@/api/notifications";
import { Card, SectionLabel } from "@/components/ui/Card";
import { LoadError } from "@/components/ui/LoadError";

// Common IANA timezones for quick-pick. Full list available via datalist autocomplete.
const COMMON_TIMEZONES = [
  "UTC",
  "Europe/London",
  "Europe/Berlin",
  "Europe/Paris",
  "Europe/Madrid",
  "Europe/Rome",
  "Europe/Amsterdam",
  "Europe/Stockholm",
  "Europe/Oslo",
  "Europe/Copenhagen",
  "Europe/Helsinki",
  "Europe/Moscow",
  "Europe/Kyiv",
  "Europe/Warsaw",
  "Europe/Athens",
  "Europe/Lisbon",
  "America/New_York",
  "America/Chicago",
  "America/Denver",
  "America/Los_Angeles",
  "America/Toronto",
  "America/Vancouver",
  "America/Mexico_City",
  "America/Sao_Paulo",
  "America/Buenos_Aires",
  "Asia/Tokyo",
  "Asia/Shanghai",
  "Asia/Hong_Kong",
  "Asia/Singapore",
  "Asia/Dubai",
  "Asia/Bangkok",
  "Asia/Kolkata",
  "Asia/Seoul",
  "Asia/Tbilisi",
  "Australia/Sydney",
  "Australia/Melbourne",
  "Pacific/Auckland",
];

export function TimezoneSection() {
  const { t } = useTranslation();
  const qc = useQueryClient();
  const profileQ = useQuery({ queryKey: ["profile"], queryFn: getProfile });
  const [saving, setSaving] = useState(false);
  const [toast, setToast] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [draftTz, setDraftTz] = useState<string>("");
  const inflight = useRef(false);

  const currentTz = profileQ.data?.timezone ?? "UTC";

  function isValidTz(tz: string): boolean {
    try {
      // Browser validates IANA timezones via Intl.
      Intl.DateTimeFormat(undefined, { timeZone: tz });
      return true;
    } catch {
      return false;
    }
  }

  async function handleSave(newTz: string) {
    if (inflight.current || newTz === currentTz) return;
    if (!isValidTz(newTz)) {
      setError(t("settings.timezone_invalid"));
      setToast(null);
      return;
    }
    inflight.current = true;
    setSaving(true);
    setError(null);
    setToast(null);
    try {
      await updateProfile({ timezone: newTz });
      await Promise.all([
        qc.invalidateQueries({ queryKey: ["profile"] }),
        qc.invalidateQueries({ queryKey: NOTIFICATION_PREFERENCES_KEY }),
      ]);
      setToast(t("settings.timezone_changed", { tz: newTz }));
      setDraftTz("");
    } catch {
      setError(t("settings.timezone_save_failed"));
    } finally {
      inflight.current = false;
      setSaving(false);
    }
  }

  return (
    <Card>
      <SectionLabel>{t("settings.timezone")}</SectionLabel>
      <div className="mt-2 text-[12px] text-taupe">{t("settings.timezone_help")}</div>
      {profileQ.isError && <LoadError onRetry={() => void profileQ.refetch()} retrying={profileQ.isFetching} />}
      <div className="mt-3 text-[14px] text-navy-deep">
        <span className="font-medium">{currentTz}</span>
      </div>
      <div className="mt-3">
        <input
          list="tz-options"
          type="text"
          aria-label={t("settings.timezone")}
          value={draftTz}
          onChange={(e) => setDraftTz(e.target.value)}
          onBlur={() => {
            if (draftTz && draftTz !== currentTz) {
              handleSave(draftTz);
            }
          }}
          onKeyDown={(e) => {
            if (e.key === "Enter" && draftTz && draftTz !== currentTz) {
              e.preventDefault();
              handleSave(draftTz);
            }
          }}
          placeholder={currentTz}
          disabled={saving || profileQ.isPending || profileQ.isError}
          className="w-full rounded-lg border border-cream-border bg-cream px-3 py-2 text-[14px] text-navy-deep placeholder:text-taupe focus:border-amber focus:outline-none disabled:opacity-60"
        />
        <datalist id="tz-options">
          {COMMON_TIMEZONES.map((tz) => (
            <option key={tz} value={tz} />
          ))}
        </datalist>
      </div>
      {error ? (
        <div role="alert" className="mt-2 rounded-md bg-red-50 px-3 py-1.5 text-[12px] text-red-700">
          {error}
        </div>
      ) : null}
      {toast ? (
        <div role="status" className="mt-2 rounded-md bg-amber/10 px-3 py-1.5 text-[12px] text-amber-dark">
          {toast}
        </div>
      ) : null}
    </Card>
  );
}
